/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import dayjs from 'dayjs';

import { DateInput, Form, Grid } from '@mairistem/composants';
import { AdresseForm } from '@mairistem/adresses';

import { SectionHeader } from '@/components/Common';
import { useAdresseFormFiche } from '@/hooks';
import { validationInformationsDissolution } from '@/utils/validations';
import { MOTIFS_DISSOLUTION } from '@/constants';

import PanelModalEdition from '../../PanelModalEdition';
import CommuneDelegueField from '../CommuneDelegueField';

type DissolutionDateField = {
  field: string;
  testId: string;
  label: string;
  // Change selon motif dissolution
  errorKey: 'dateMariage' | 'dateDeces' | 'dateDeclaration';
};

/**
 * Dates à saisir selon le motif de dissolution
 */
export const getDissolutionDateFields = (
  identifiantTypeDissolution: number,
): DissolutionDateField[] => {
  if (identifiantTypeDissolution >= 1 && identifiantTypeDissolution <= 3) {
    return [
      {
        field: 'dateMariage',
        testId: 'date-mariage',
        label: 'Date de mariage',
        errorKey: 'dateMariage',
      },
    ];
  }
  if (identifiantTypeDissolution === 4) {
    return [
      {
        field: 'partenaire1DateDeces',
        testId: 'date-deces-partenaire-1',
        label: 'Décès du partenaire 1',
        errorKey: 'dateDeces',
      },
      {
        field: 'partenaire2DateDeces',
        testId: 'date-deces-partenaire-2',
        label: 'Décès du partenaire 2',
        errorKey: 'dateDeces',
      },
    ];
  }
  if (identifiantTypeDissolution === 5) {
    return [
      {
        field: 'partenaire1DateDeces',
        testId: 'date-deces-partenaire-1',
        label: 'Décès du partenaire 1',
        errorKey: 'dateDeces',
      },
    ];
  }
  if (identifiantTypeDissolution === 6) {
    return [
      {
        field: 'partenaire2DateDeces',
        testId: 'date-deces-partenaire-2',
        label: 'Décès du partenaire 2',
        errorKey: 'dateDeces',
      },
    ];
  }
  if (identifiantTypeDissolution >= 7 && identifiantTypeDissolution <= 10) {
    return [
      {
        field: 'dateDeclarationUnitDi',
        testId: 'date-decision-unilaterale',
        label: 'Décision unilatérale',
        errorKey: 'dateDeclaration',
      },
      {
        field: 'dateSignification',
        testId: 'date-signification',
        label: 'Signification',
        errorKey: 'dateDeclaration',
      },
    ];
  }
  if (identifiantTypeDissolution >= 11 && identifiantTypeDissolution <= 12) {
    return [
      {
        field: 'dateDeclarationJointDi',
        testId: 'date-declaration-conjointe',
        label: 'Déclaration conjointe',
        errorKey: 'dateDeclaration',
      },
    ];
  }
  return [];
};

const ALL_DISSOLUTION_DATE_FIELDS = [
  'dateMariage',
  'partenaire1DateDeces',
  'partenaire2DateDeces',
  'dateDeclarationUnitDi',
  'dateSignification',
  'dateDeclarationJointDi',
];

interface InformationsDissolutionModalProps {
  data: any;
  onSave: () => Promise<void>;
  onChange: (data: Record<string, unknown>) => void;
  onClose: () => void;
  onCancel: () => void;
}

const InformationsDissolutionModal = (
  props: InformationsDissolutionModalProps,
) => {
  const { data, onSave, onChange, onClose, onCancel } = props;

  const [loading, setLoading] = React.useState<boolean>(false);
  const [errors, setErrors] = React.useState<
    Record<string, string | boolean> & { api?: boolean }
  >({});

  const dateFields = React.useMemo(
    () => getDissolutionDateFields(data?.identifiantTypeDissolution),
    [data?.identifiantTypeDissolution],
  );

  const handleChangeMotif = React.useCallback(
    (value: number) => {
      setErrors({});
      const keptFields = new Set(
        getDissolutionDateFields(value).map((dateField) => dateField.field),
      );
      const clearedDates = ALL_DISSOLUTION_DATE_FIELDS.reduce(
        (acc, field) => {
          if (!keptFields.has(field)) {
            acc[field] = null;
          }
          return acc;
        },
        {} as Record<string, null>,
      );
      onChange({ identifiantTypeDissolution: value, ...clearedDates });
    },
    [onChange],
  );

  const handleChangeDate = React.useCallback(
    (field: string, date: string | Date | undefined) => {
      setErrors({});
      onChange({ [field]: date ? dayjs(date).format('YYYY-MM-DD') : null });
    },
    [onChange],
  );

  const handleChangeDateDissolution = React.useCallback(
    (date: string | Date | undefined) => {
      setErrors({});
      onChange({
        dateDissolution: date ? dayjs(date).format('YYYY-MM-DD') : null,
      });
    },
    [onChange],
  );

  const handleAdresseChange = React.useCallback(
    (adresse: Record<string, unknown>) =>
      onChange({
        adresseDissolution: { ...data?.adresseDissolution, ...adresse },
      }),
    [onChange, data?.adresseDissolution],
  );
  const adresseForm = useAdresseFormFiche(
    data?.adresseDissolution,
    handleAdresseChange,
    { articleField: 'articleLocalite' },
  );

  const handleChangeCommuneDelegue = React.useCallback(
    (libelle: string, drapeau: boolean) => {
      onChange({
        adresseDissolution: {
          ...data?.adresseDissolution,
          communeDelegue: libelle,
          estCommuneDelegue: drapeau,
        },
      });
    },
    [onChange, data?.adresseDissolution],
  );

  const handleSave = React.useCallback(() => {
    const validationErrors = validationInformationsDissolution({
      conventionDeclaration: data,
    });

    if (!_.isEmpty(validationErrors)) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);

    onSave()
      .then(() => {
        onClose();
      })
      .catch(() => {
        setErrors({ api: true });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [data, onClose, onSave]);

  return (
    <PanelModalEdition
      data-testid="dissolution-modal"
      errors={errors}
      loading={loading}
      header="INFORMATIONS DE DISSOLUTION"
      onSave={handleSave}
      onClose={onCancel}
    >
      <Form>
        <Grid>
          <Grid.Row>
            <Grid.Column width={16}>
              <SectionHeader
                title="MOTIF DE DISSOLUTION"
                style={{ margin: 0 }}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row style={{ marginLeft: '1em' }}>
            <Grid.Column width={6}>
              <Form.Dropdown
                selection
                fluid
                label="Dissolution par"
                data-testid="dissolution-motif"
                options={MOTIFS_DISSOLUTION}
                value={data?.identifiantTypeDissolution ?? 0}
                error={!!errors?.motifDissolution}
                onChange={(_e: any, target: any) =>
                  handleChangeMotif(Number(target?.value))
                }
              />
            </Grid.Column>
            {dateFields.map((dateField) => (
              <Grid.Column
                width={dateFields.length === 1 ? 10 : 5}
                key={dateField.field}
              >
                <Form.Field error={!!errors?.[dateField.errorKey]}>
                  <label>{dateField.label}</label>
                  <DateInput
                    data-testid={`dissolution-${dateField.testId}`}
                    value={
                      data?.[dateField.field]
                        ? new Date(data[dateField.field])
                        : undefined
                    }
                    onChange={(date) => handleChangeDate(dateField.field, date)}
                  />
                </Form.Field>
              </Grid.Column>
            ))}
          </Grid.Row>
          <Grid.Row style={{ marginLeft: '1em' }}>
            <Grid.Column width={6}>
              <Form.Field error={!!errors?.dateDissolution}>
                <label>Date de dissolution</label>
                <DateInput
                  data-testid="dissolution-date"
                  value={
                    data?.dateDissolution
                      ? new Date(data.dateDissolution)
                      : undefined
                  }
                  onChange={handleChangeDateDissolution}
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={16}>
              <SectionHeader
                title="LIEU DE DISSOLUTION"
                style={{ marginTop: 0 }}
              />
              <div
                style={{
                  marginLeft: '1em',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1em',
                }}
              >
                <AdresseForm
                  saisie="libre"
                  handleTypes
                  displayDepartement={false}
                  displayArticle
                  adresse={adresseForm.adresseFormValue}
                  onItemChange={adresseForm.onItemChange}
                  onChange={adresseForm.onChange}
                />
                {/* Même espacement (2em) qu'entre les lignes d'AdresseForm */}
                <div style={{ marginTop: '2em' }}>
                  <CommuneDelegueField
                    data-testid="dissolution-commune-delegue"
                    libelle={data?.adresseDissolution?.communeDelegue}
                    drapeau={data?.adresseDissolution?.estCommuneDelegue}
                    onChange={handleChangeCommuneDelegue}
                  />
                </div>
              </div>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Form>
    </PanelModalEdition>
  );
};

export default InformationsDissolutionModal;
